import classNames from "classnames";
import { NavLink, useNavigate } from "react-router-dom";
import { useNavs } from "../../customHooks/useNavs";
import { isProfileApproved } from "../../helperFunctions";
import { useGetUserProfileQuery } from "../../redux/features/auth/auth.api";
import { AuthSliceActions } from "../../redux/features/auth/auth.slice";
import { layoutSliceActions } from "../../redux/features/layout/layout.slice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { Icon } from "../icons/icon";
import { EmptySideNavFeaturesState } from "../states/emptySidenavFeatures";
import { ProfileNotificationComponent } from "./profileNotificationComp";

export const SideNav = ({
  navOpen,
  setNavOpen,
  className = "",
}: {
  navOpen: boolean;
  setNavOpen: (val: boolean) => void;
  className?: string;
}) => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const profile = useAppSelector((state) => {
    return state.auth.profile;
  });

  const popupOpen = useAppSelector((state) => {
    return state.layout.onboardingPopupOpen;
  });

  const { navs } = useNavs();

  useGetUserProfileQuery(undefined, {});

  return (
    <>
      <div
        className={classNames(
          `
          bg-white h-full overflow-x-visible
          absolute lg:relative
          min-w-[272px] max-w-[272px]
          border-l lg:border-r border-light-grey
          p-6 flex flex-col gap-10 items-center
          transition-all duration-500
          `,
          {
            "left-0 flex": navOpen,
            "-left-[300px] lg:left-0 lg:flex": !navOpen,
            "z-[100]": popupOpen,
            "z-[999]": !popupOpen,
          },
          className
        )}
      >
        {/* Top stuff */}
        <div className="flex gap-3 items-center w-full">
          {/* Logo */}
          <div
            className={classNames(
              `
              transition-all delay-200 duration-500 cursor-pointer
              flex flex-row items-center justify-between flex-1
              `,
              {
                "translate-x-0 opacity-100": navOpen,
                "-translate-x-8 lg:translate-x-0 opacity-0 lg:opacity-100":
                  !navOpen,
              }
            )}
          >
            <img
              src="/assets/images/logo.svg"
              alt="logo"
              className="md:hidden block"
              onClick={() => {
                navigate("/app/overview");
              }}
            />

            <img
              src="/assets/images/logo_desktop.svg"
              alt="logo"
              className="hidden md:block"
              onClick={() => {
                navigate("/app/overview");
              }}
            />
          </div>

          <button
            type="button"
            onClick={() => setNavOpen(false)}
            className="lg:hidden w-fit"
          >
            <Icon svg="cancel-icon" width={24} height={24} />
          </button>

          <div className="hidden lg:flex">
            <ProfileNotificationComponent />
          </div>
        </div>

        {/* Menus */}
        <div className="w-full flex flex-col gap-4">
          {navs.map(({ title, icon, to, hasCount, count }, idx) => {
            const key = `nav-item-${idx}`;

            return (
              <NavLink
                key={key}
                to={to}
                onClick={() => setNavOpen(false)}
                className={({ isActive }) => {
                  const activeClassName = isActive
                    ? "!text-pry-green-200 !bg-light-green-bg-200 font-medium"
                    : "";

                  return `
                    ${activeClassName}
                    group flex items-center gap-3
                    rounded-lg py-3 px-4 text-sm
                    hover:text-pry-green-200 hover:font-medium
                    hover:bg-light-grey-bg
                    font-medium transition-all
                        `;
                }}
              >
                <Icon
                  svg={icon}
                  width={20}
                  height={20}
                  className="
                    lg:group-hover:scale-[0.9]
                    g:group-hover:text-pry-green-200
                    transition-all duration-[700ms]
                    "
                />

                <p className="flex-1">{title}</p>

                {hasCount && (count ?? 0) > 0 ? (
                  <p
                    className={`
                        bg-info-purple text-white
                        p-1 rounded-full
                        text-xs
                        h-[20px] min-w-[20px]
                        flex items-center justify-center
                        font-medium
                `}
                  >
                    {count}
                  </p>
                ) : undefined}
              </NavLink>
            );
          })}

          {!isProfileApproved(profile) && <EmptySideNavFeaturesState />}
        </div>

        <button
          type="button"
          className="
            flex gap-3 items-center text-sm
            font-medium text-red-200
            cursor-pointer py-3 px-4
            rounded-lg hover:bg-lighter-red-bg
            group mb-0 md:mb-0 transition-all
            w-full mt-auto
            "
          onClick={() => {
            dispatch(layoutSliceActions.reset());
            dispatch(AuthSliceActions.logout());
          }}
        >
          <Icon
            svg={"exit"}
            width={20}
            height={20}
            className="
              lg:group-hover:scale-[0.9]
              transition-all duration-[700ms]
              "
          />
          <p>Logout</p>
        </button>
      </div>

      {navOpen && (
        <div
          className="
            fixed inset-0 lg:hidden bg-black/50
            transition-all duration-200 z-[998]
            "
          onClick={() => {
            setNavOpen(false);
          }}
        />
      )}
    </>
  );
};
